import { useCallback, useEffect, useRef, useState } from 'react'
import { sileo } from 'sileo'
import { solutionService } from '../services/solutionService'

/** Espera tras la última tecla antes de buscar (la búsqueda va al servidor). */
const DEBOUNCE_MS = 300
const PAGE_SIZE = 30

/**
 * Búsqueda en la base de soluciones: texto con espera, páginas acumulables
 * ("Cargar más") y total de resultados.
 *
 * @returns {{
 *   query: string, setQuery: (q: string) => void,
 *   items: Array<Object>, total: number,
 *   loading: boolean, hasMore: boolean, loadMore: () => void
 * }}
 */
export default function useSolutionSearch() {
  const [query, setQueryState] = useState('')
  const [appliedQuery, setAppliedQuery] = useState('')
  const [page, setPage] = useState(0)
  const [items, setItems] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const timer = useRef(null)

  useEffect(() => () => clearTimeout(timer.current), [])

  /** Actualiza el campo y busca tras una breve espera (vuelve a la página 0). */
  const setQuery = useCallback((value) => {
    setQueryState(value)
    clearTimeout(timer.current)
    timer.current = setTimeout(() => {
      setLoading(true)
      setPage(0)
      setAppliedQuery(value.trim())
    }, DEBOUNCE_MS)
  }, [])

  useEffect(() => {
    let active = true
    solutionService
      .search({ query: appliedQuery, page, pageSize: PAGE_SIZE })
      .then(({ success, data, error }) => {
        if (!active) return
        if (!success) {
          sileo.error({
            title: 'Error cargando soluciones',
            description: error,
          })
        } else {
          setItems((prev) =>
            page === 0 ? data.items : [...prev, ...data.items]
          )
          setTotal(data.total)
        }
        setLoading(false)
      })
    return () => {
      active = false
    }
  }, [appliedQuery, page])

  const loadMore = useCallback(() => {
    setLoading(true)
    setPage((p) => p + 1)
  }, [])

  return {
    query,
    setQuery,
    items,
    total,
    loading,
    hasMore: items.length < total,
    loadMore,
  }
}
